import * as THREE from 'three';
import { hash3 } from './fishSwim';

/**
 * The animals' continuity on the Reef (M266, the user's brief "Persistent Animals, Natural Wildlife Visits & Follow
 * Animal Mode": "an animal should disappear from the player's view because something meaningful happened, not merely
 * because five seconds passed"). The simulation's counts stay its own; these decide only how the drawing follows them.
 * Each figure is a rendering choice (JUDGED) unless it says otherwise.
 */

/**
 * How far a group's exact share of the hectare (`ReefFish.exact`) may stray from the count drawn before the drawing
 * takes the new rounded count, fish: half a fish, the rounding's own, and `margin` more (JUDGED). MEASURED before M266
 * (seed 42, 120 days from a new game): 90 of 120 days changed some island's count of some group, 151 changes, most a
 * share on a rounding's edge stepping one fish up and back (the taʻape's young 33 times, the bullethead's 19, the
 * redlip's terminal males 18). After a jump of `resetDays` days or more (JUDGED: two months, past which the counts held are
 * no longer yesterday's), or another world laid on the scene, the counts are taken afresh.
 */
export const STEADY = { margin: 0.35, resetDays: 60 } as const;

/**
 * The count drawn for a group today (M266): yesterday's while today's exact share lies within half a fish and
 * `STEADY.margin` of it; else today's rounded count. So a share moving through a rounding's edge changes the drawing once,
 * and the drawing is never more than `0.5 + margin` of a fish off the share it stands for.
 */
export function steadyCount(before: number | undefined, exact: number, rounded: number): number {
  if (before === undefined || !Number.isFinite(exact)) return rounded;
  return Math.abs(exact - before) < 0.5 + STEADY.margin ? before : rounded;
}

/**
 * How the fish drawn follow a new day's counts (M266): each fish coming in or leaving faded over `fadeS` seconds (a
 * two-second fade, as the visitors' `VISITOR_DRAW.fadeS`); a kind's number of schools on an island kept while its fish
 * stay within half a school and `schoolMargin` more of it (JUDGED), so one fish's coming or going splits or joins no
 * school.
 */
export const RECONCILE = { fadeS: 2, schoolMargin: 0.25 } as const;

/**
 * Each visiting kind's mean visit, days (M266, JUDGED: the drawing's, so a visitor stays a coherent while and is then
 * gone; a real pass through a hectare takes minutes, which the game's five-second day cannot show). A manta's one day,
 * as before: its odds are the share of divers' surveys that met one (`MANTA.visitsSeenShare`), not a share of its time,
 * so no visit's length is read into them. A whitetip lives on its reef (M235): long stays.
 */
export const VISIT_DAYS = { honu: 6, honuea: 4, seal: 3, tigerShark: 2.5, whitetip: 12, manta: 1 } as const;
export type VisitKind = keyof typeof VISIT_DAYS;

/** A visit (M266): its first day and the day after its last, epoch days. */
export interface Visit {
  start: number;
  end: number;
}

/**
 * The visit of a kind covering a day, if any (M266, in place of a draw each day): the days cut into slots of `S` days,
 * each slot holding at most one visit, of a length drawn between half the slot and all of it, at a place in the slot of
 * its own; each slot's chance of one set from the odds on its first day so the share of days a visitor is there is the
 * odds (`odds` the share of days one is in the scene: an instantaneous occupancy, its density times the scene's area,
 * for the honu, the hawksbill, the tiger shark and the whitetip; the seal's the user's 0.2; the manta's a survey's
 * sighting share, M242). Longer visits, fewer of them: the same share of days, never more (the user's word: "a tiger
 * shark should remain a rare encounter"). No two visits of a kind overlap. A pure function of the day and `salt`, so
 * the same on every opening of the scene and every reload.
 */
export function visitOn(meanDays: number, odds: (epochDay: number) => number, epochDay: number, salt: number): Visit | null {
  const slot = meanDays <= 1 ? 1 : Math.max(2, Math.round(meanDays / 0.75));
  const offset = Math.floor(hash3(salt, 0, 266) * slot);
  const start = Math.floor((epochDay - offset) / slot) * slot + offset;
  const p = odds(start);
  if (!(p > 0)) return null;
  // Its length: between half the slot and all of it, evenly, the mean `(least + slot) / 2`; or, where the odds ask more
  // than slots of that mean can give, every slot a visit as long as the odds' share of it.
  const least = Math.ceil(slot / 2);
  const mean = (least + slot) / 2;
  const fill = (p * slot) / mean;
  // (Its length then the odds' share of the slot on average, a day more or less as a draw gives it.)
  const length = fill > 1 ? Math.max(1, Math.min(slot, Math.floor(p * slot) + (hash3(start, salt, 2) < p * slot - Math.floor(p * slot) ? 1 : 0))) : least + Math.floor(hash3(start, salt, 2) * (slot - least + 1));
  if (fill <= 1 && hash3(start, salt, 1) >= fill) return null;
  const from = start + Math.floor(hash3(start, salt, 3) * (slot - length + 1));
  return epochDay >= from && epochDay < from + length ? { start: from, end: from + length } : null;
}

/**
 * Each fish's place in the level of detail held (M266, JUDGED): drawn until the level falls a share `margin` of itself
 * below its key, and not drawn again until it rises that share above it, so the level's small swings as fish swim in
 * and out of view turn no fish on and off (before M266 none was held: a fish whose key sat at the level was drawn by
 * its key's band, faded in and out as the view's cover moved); faded in or out over `fadeS` seconds whatever the zoom's speed.
 */
export const LOD_HOLD = { margin: 0.1, fadeS: 0.6 } as const;

/** Whether a fish of draw `key` is drawn at the level `keep`, held as it was (`on`) within `LOD_HOLD.margin`; every fish at the whole level. */
export function heldInLevel(on: boolean, key: number, keep: number): boolean {
  if (keep >= 1) return true;
  if (on) return key <= keep * (1 + LOD_HOLD.margin);
  return key < keep * (1 - LOD_HOLD.margin);
}

/**
 * Whether a point is in a camera's view, within `visible`, the part of the screen the panels leave in device coordinates
 * (left, right, bottom, top; the whole screen by default), in front of the camera and short of its far plane (review 31:
 * one helper where the visitors, the hunts' stage, their follow and their returning fish each had their own, one of them
 * taking a point behind the camera for one in view). `scratch` spares a frame's allocation.
 */
export function inView(camera: THREE.Camera, at: THREE.Vector3, visible: readonly [number, number, number, number] = [-1, 1, -1, 1], scratch = new THREE.Vector3()): boolean {
  const p = scratch.copy(at).project(camera);
  return p.z > -1 && p.z < 1 && p.x >= visible[0] && p.x <= visible[1] && p.y >= visible[2] && p.y <= visible[3];
}
